import CreditMenuItem from '../LeftMenuBar/Credit/CreditMenuItem';
import LayerMenuItem from '../LeftMenuBar/Layer/LayerMenuItem';
import ToolSettingMenuItem from '../LeftMenuBar/ToolSetting/ToolSettingMenuItem';
import MenuList from '../LeftMenuBar/MenuList';
import WeatherMenuItem from '../LeftMenuBar/Weather/WeatherMenuItem';
import LegendMenuItem from '../LeftMenuBar/Legend/LegendMenuItem';
import MapViewContainer from '../MapViewContainer';
import React, { useContext, useMemo } from 'react';
import { DuiContext } from '../Context';
import { JSDCContext } from '../../JSDC/Context';
import { latLng } from 'leaflet';
import TreeSearchMenuItem from '../LeftMenuBar/TreeSearch/TreeSearchMenuItem';
import AboutWalkMenuItem from '../LeftMenuBar/AboutWalk/AboutWalkMenuItem';
import { DguidewalksContext } from '../../JSDC/Dguidewalks/Context';
import SceneMenuItem from '../LeftMenuBar/Scene/SceneMenuItem';
// import VisitorCount from '../VisitorCount'
import { mapKeys, omit, pick } from 'lodash';
import { GeoJsonProperties } from 'geojson';
import { Article } from '../../JSDC/Dguidewalks/proxyParser/@types';

export interface IDguideWalksAppProps {
  mainMenuChildren?: React.ReactNode;
  endMenuChildren?: React.ReactNode;
}

const DguideWalksApp: React.FC<IDguideWalksAppProps> = ({ mainMenuChildren, endMenuChildren }) => {
  const {
    dgw: { layerNameOrder },
  } = useContext(DguidewalksContext);
  const dui = useContext(DuiContext);
  const { Jsdc, layerInfos, trailQuery, advanceListingQuery } = useContext(JSDCContext);

  const orderedLayerInfos = useMemo(() => {
    const layerInfoMap = mapKeys(layerInfos, (info) => info.description.name);
    const matchedInfos = Object.values(pick(layerInfoMap, layerNameOrder));
    const restInfos = Object.values(omit(layerInfoMap, layerNameOrder));
    return [...matchedInfos, ...restInfos];
  }, [layerInfos, layerNameOrder]);

  // 環境紀錄列表 = 目前兩個圖層實際顯示的點位（鏡射地圖）。
  // 因此自動與「河溪搜尋」結果、以及「環境紀錄/進階環境紀錄」圖層勾選連動：
  // 搜尋→只列命中；取消勾選某類→該類圖層被清空，列表也不列該類。
  // 只用 occurrence_url（= /node/{nid}，每筆唯一）比對；occurrence_id 是河溪名稱、
  // 同一條溪多筆共用，拿來 match 會誤把其他類型的紀錄一起帶出。
  const sceneCardsReducer = (data: Article[]): Article[] => {
    const base = dui.sceneCardsReducer ? dui.sceneCardsReducer(data) : data;
    const matched = new Set<string>();
    const collect = (layer: typeof trailQuery.trailsLayer) =>
      layer.forEachLayerAsGeoJSON<unknown, GeoJsonProperties>((_leafletLayer, props) => {
        if (props?.occurrence_url) matched.add(String(props.occurrence_url));
      });
    collect(trailQuery.trailsLayer);
    collect(advanceListingQuery.advanceListingLayer);
    return base.filter((a) => !!a.link && matched.has(a.link));
  };

  return (
    <MapViewContainer
      Jsdc={Jsdc}
      headerImgSrc={dui.headerMBImgSrc}
      menuChildren={
        <MenuList
          title={dui.sidebarTitle}
          subtitle={dui.sidebarSubtitle}
          headerImg={dui.headerDImgSrc}
          headerMBImg={dui.headerMBImgSrc}
          endChildren={
            <>
              <AboutWalkMenuItem
                imgSrc={dui.aboutWalkImgSrc}
                title={dui.sidebarTitle}
                subtitle={dui.sidebarSubtitle}
                content={dui.aboutWalkContent}
                active={dui.activeMenuId === '圖臺介紹'}
                {...dui.menuSwitcherAction('圖臺介紹')}
              />
              <CreditMenuItem
                active={dui.activeMenuId === '關於圖臺'}
                {...dui.menuSwitcherAction('關於圖臺')}
                herf={dui.creditHref}
                description={dui.credit}
              />
              {endMenuChildren}
            </>
          }
        >
          <>
            <LayerMenuItem
              layerInfos={orderedLayerInfos.map((item) => ({
                id: item.id,
                type: item.description.type,
                name: item.description.name,
                show: item.show,
              }))}
              onToggleShow={(id, show) => (Jsdc.Controller.get('Layer').getById(id).show = show)}
              onOpacityChange={(id, opacity) =>
                Jsdc.Controller.get('Layer')
                  .getById(id)
                  .setOpacity(Number(1 - opacity / 100))
              }
              active={dui.activeMenuId === '地圖圖層'}
              {...dui.menuSwitcherAction('地圖圖層')}
            />
            <TreeSearchMenuItem
              active={dui.activeMenuId === '河溪搜尋'}
              {...dui.menuSwitcherAction('河溪搜尋')}
            />
            <SceneMenuItem
              onTarget={dui.onSceneTargetClick}
              onNavigate={dui.onSceneNavigate}
              cardsReducer={sceneCardsReducer}
              active={dui.activeMenuId === '環境紀錄'}
              {...dui.menuSwitcherAction('環境紀錄')}
            />
            {dui.weatherConfig.disabled || (
              <WeatherMenuItem
                active={dui.activeMenuId === '氣象預測'}
                {...dui.menuSwitcherAction('氣象預測')}
                locations={dui.weatherConfig.locations}
                token={dui.weatherConfig.token!}
                onSelectLocation={([y, x]) => Jsdc.viewer?.flyTo(latLng(y, x), 13)}
              />
            )}
            {dui.legendConfig.disabled || (
              <LegendMenuItem
                activeLegends={dui.legendConfig.activeLegends}
                active={dui.activeMenuId === '圖例說明'}
                {...dui.menuSwitcherAction('圖例說明')}
              />
            )}
            {
              <ToolSettingMenuItem
                active={dui.activeMenuId === '工具設定'}
                {...dui.menuSwitcherAction('工具設定')}
              />
            }
            {mainMenuChildren}
          </>
        </MenuList>
      }
    />
  );
};
DguideWalksApp.displayName = 'DguideWalksApp';
export default DguideWalksApp;
